import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Download, FileText } from 'lucide-react';
import { getFatturaPassiva, getFilePassivaBlob, type FatturaPassiva } from '../../api/fatturePassive';

const eur = (n: number) => `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Props {
  fatturaId: string;
  onClose: () => void;
}

export default function FatturaPassivaDettaglioModal({ fatturaId, onClose }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [d, setD] = useState<FatturaPassiva | null>(null);
  const [error, setError] = useState<string | null>(null);

  function apriConferimento(id: string) {
    onClose();
    navigate(`/conferimenti/${id}`);
  }

  useEffect(() => {
    getFatturaPassiva(fatturaId).then(setD).catch(() => setError(t('fatturazione.passiva_dettaglio.errore')));
  }, [fatturaId, t]);

  async function scarica() {
    if (!d) return;
    const blob = await getFilePassivaBlob(d.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = d.file_name ?? `fattura_passiva_${d.id}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <FileText size={16} className="text-sage" /> {t('fatturazione.passiva_dettaglio.titolo', { numero: d?.numero_fornitore ?? '' })}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>
        {error ? (
          <div className="px-6 py-8 text-sm text-accent-danger">{error}</div>
        ) : !d ? (
          <div className="px-6 py-8 text-sm text-ink-soft">{t('fatturazione.passiva_dettaglio.caricamento')}</div>
        ) : (
          <div className="px-6 py-5 space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <div><span className="text-ink-soft">{t('fatturazione.passiva_dettaglio.fornitore')}</span> <span className="font-medium">{d.customer_ragione_sociale ?? '—'}</span></div>
              <div><span className="text-ink-soft">{t('fatturazione.passiva_dettaglio.n_fattura')}</span> {d.numero_fornitore}</div>
              <div><span className="text-ink-soft">{t('fatturazione.passiva_dettaglio.data')}</span> {new Date(d.data).toLocaleDateString('it-IT')}</div>
              <div><span className="text-ink-soft">{t('fatturazione.passiva_dettaglio.importo')}</span> <span className="font-medium">{eur(d.importo)}</span></div>
              <div><span className="text-ink-soft">{t('fatturazione.passiva_dettaglio.stato')}</span> {d.pagata ? (d.data_pagamento ? t('fatturazione.passiva_dettaglio.pagata_il', { data: new Date(d.data_pagamento).toLocaleDateString('it-IT') }) : t('fatturazione.passiva_dettaglio.pagata')) : t('fatturazione.passiva_dettaglio.da_pagare')}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-ink-soft mb-1">{t('fatturazione.passiva_dettaglio.conferimenti_collegati', { n: d.conferimenti.length })}</p>
              <ul className="text-sm space-y-0.5">
                {d.conferimenti.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => apriConferimento(c.id)}
                      className="text-sage-dark hover:underline font-medium" data-testid={`conf-link-${c.id}`}>
                      {c.numero ?? c.id.slice(0, 8)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {d.uscite.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ink-soft mb-1" data-testid="uscite-collegate">
                  {t('fatturazione.passiva_dettaglio.uscite_collegate', { n: d.uscite.length })}
                </p>
                <ul className="text-sm space-y-0.5">
                  {d.uscite.map((u) => (
                    <li key={u.id}>
                      <button type="button" onClick={() => { onClose(); navigate(`/uscite/${u.id}`); }}
                        className="text-sage-dark hover:underline font-medium" data-testid={`uscita-link-${u.id}`}>
                        {u.codice}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {d.has_file && (
              <button type="button" onClick={scarica} className="inline-flex items-center gap-1 text-sm text-sage-dark hover:underline">
                <Download size={14} /> {t('fatturazione.passiva_dettaglio.scarica_file', { name: d.file_name ?? t('fatturazione.passiva_dettaglio.allegato') })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
